import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PauseIcon from "assets/pause.svg?react";
import PlayIcon from "assets/play.svg?react";

import type { NestedNode } from "screens/call/callscript-graph-to-nested";

import styles from "./index.module.css";
import { IconButton } from "components/icon-button";

const MAX_TIME = 3000;
const TIMER_INTERVAL = 100;
const STEP = 100 / (MAX_TIME / TIMER_INTERVAL);

type NodeProps = {
  node: NestedNode;
  onSelectOption?: CallableFunction;
};

export const TimedProgressBar = ({ onSelectOption, node }: NodeProps) => {
  const [progress, setProgress] = useState(0);
  const [play, setPlay] = useState(true);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onSelectOptionRef = useRef(onSelectOption);
  const nodeRef = useRef(node);

  useLayoutEffect(() => {
    onSelectOptionRef.current = onSelectOption;
    nodeRef.current = node;
  });

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (!play) {
        return;
      }
      setProgress((prev) => {
        if (prev + STEP >= 100) {
          if (timerRef.current) clearInterval(timerRef.current);
          onSelectOptionRef.current?.(nodeRef.current.id);
          return 100;
        }
        return prev + STEP;
      });
    }, TIMER_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [play]);

  const handleTogglePlay = useCallback(() => {
    setPlay((p) => !p);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === "Space") {
        event.preventDefault();
        handleTogglePlay();
      }
    },
    [handleTogglePlay],
  );

  useEffect(() => {
    const targetWindow = containerRef.current?.ownerDocument.defaultView ?? window;
    targetWindow.addEventListener("keydown", handleKeyDown);
    return () => targetWindow.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div ref={containerRef} className={styles.progressContainer}>
      <div className={styles.inner}>
        <div
          className={styles.progress}
          style={{
            width: `${progress}%`,
          }}
        />
      </div>
      <div className={styles.buttonContainer}>
        {play ? (
          <IconButton variant="white" onClick={handleTogglePlay}>
            <PauseIcon />
          </IconButton>
        ) : (
          <IconButton variant="white" onClick={handleTogglePlay}>
            <PlayIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
};
